import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { compose } from 'redux';
import { Route } from 'react-router-dom';

import {
  Button,
  MaterialIcon,
} from '@pearsontechnology/aero-components';

import {
  PageWrapper,
  PageViewport,
  PageContent,
  PageActions,
  PageActionsRight,
} from 'components/PageStyles';
import helpContext from 'constants/helpContext';
import { ActionTypes } from 'constants/enums';
import enhanceWithServices from 'hocs/enhanceWithServices';
import ProjectActivityList from 'Workspace/Projects/components/ProjectActivityList';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import EmptyPageBuilder from 'components/EmptyPage';
import classEmptyDarkImage from 'images/class-empty-dark.png';
import classEmptyLightImage from 'images/class-empty-light.png';
import {
  Grid,
  GridCell,
} from 'components/LayoutGrid';
import {
  commandWorkspaceProjectList,
} from 'containers/ConnectionManager/actions';
import PlaceholderCardList from 'components/PlaceholderCardList';
import TabHeader from 'components/TabHeader';
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES, GTM_EVENT_CATEGORY, GTM_EVENT_ACTION } from 'utils/gtm/constants';
import { gtmTrackAuthoringCourseContentUserInteraction, gtmTrackAuthoringDashboardUserInteraction } from 'utils/gtm/handler';
import { setCoursewareContext } from 'utils/coursewareContext';
import projectServiceShape from 'services/types/projectService';
import workspaceService from 'services/WorkspaceService';
import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import { featureKeys } from 'services/FeatureFlagService';
import messages from './messages';
import {
  PlaceholderPageContent,
  ProjectRoute,
  FavoritesContainer,
  FavoritesEmptyHint,
  MainProjectsCollapsibleSection,
  MainProjectsToggleHeader,
  MainProjectsToggleTitle,
} from './styles';
import {
  loadFavoriteProjectIds,
  saveFavoriteProjectIds,
  loadAllProjectsExpanded,
  saveAllProjectsExpanded,
} from './workspaceProjectFavoriteStorage';
import { selectProjectList } from '../../store/selectors';
import ProjectCard from '../ProjectCard';
import CreateProjectModal from '../CreateProjectModal';
import DeleteConfirm from '../DeleteConfirm';
import MoveProjectModal from '../MoveProjectModal';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const EmptyPage = EmptyPageBuilder.build({
  lightBackgroundImage: classEmptyLightImage,
  darkBackgroundImage: classEmptyDarkImage,
});

const toProjectsArray = (raw) => {
  if (!raw) return [];
  if (typeof raw.toJS === 'function') return raw.toJS();
  return Array.isArray(raw) ? raw : [];
};

const ProjectsList = ({ getProjectsList, projects, projectService, workspaceList, getFeatureFlagValue}) => {
  const [isCreateProjectModalVisible, setCreateProjectModalVisible] = useState(false);
  const [isMoveProjectModalVisible, setMoveProjectModalVisible] = useState(false);
  const [isDeleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState();
  const [favoriteProjects, setFavoriteProjects] = useState([]);
  const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(true);
  const [isAllProjectsExpanded, setIsAllProjectsExpanded] = useState(true);
  const prevFavoriteCountRef = useRef(0);
  const selectedWorkspace = workspaceService.getSelectedWorkspace();
  const workspaceId = selectedWorkspace?.id;
  const isTabHeaderVisible = getFeatureFlagValue(featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22);

  useEffect(() => {
    if (location) {
      const { pathname } = location;
      gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: pathname } });
    }
  }, []);

  useEffect(() => {
    if (!isSelectedWorkspaceExist() && workspaceList) {
      workspaceService.setSelectedWorkspace(workspaceList[0]);
    }
    if (isSelectedWorkspaceExist()) {
      getProjectsList(selectedWorkspace.id);
    }
  }, [workspaceList, getProjectsList, selectedWorkspace.id]);

  const projectsArray = useMemo(() => toProjectsArray(projects), [projects]);

  useEffect(() => {
    setIsFavoritesExpanded(true);
    if (!workspaceId) {
      setIsAllProjectsExpanded(true);
      return;
    }
    setIsAllProjectsExpanded(loadAllProjectsExpanded(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setFavoriteProjects([]);
      return;
    }
    if (!projectsArray.length) {
      setFavoriteProjects([]);
      return;
    }
    const storedIds = loadFavoriteProjectIds(workspaceId);
    if (storedIds.length === 0) {
      setFavoriteProjects([]);
      return;
    }
    const resolved = storedIds
      .map((id) => projectsArray.find((p) => p.id === id))
      .filter(Boolean);
    setFavoriteProjects(resolved);
  }, [workspaceId, projectsArray]);

  useEffect(() => {
    prevFavoriteCountRef.current = 0;
  }, [workspaceId]);

  useEffect(() => {
    const n = favoriteProjects.length;
    const prev = prevFavoriteCountRef.current;
    if (n === 0 && prev > 0) {
      setIsAllProjectsExpanded(true);
      if (workspaceId) {
        saveAllProjectsExpanded(workspaceId, true);
      }
    }
    prevFavoriteCountRef.current = n;
  }, [favoriteProjects, workspaceId]);

  const isSelectedWorkspaceExist = () => Object.keys(workspaceService.getSelectedWorkspace()).length !== 0;
  
  const handleActionMenuItemClick = (actionType, projectId) => {
    setActiveProjectId(projectId);
    switch (actionType) {
      case ActionTypes.MOVE:
        openMoveProjectModal();
        break;
      case ActionTypes.DELETE:
        openDeleteConfirm();
        break;
      default:
    }
  };

  const openCreateProjectModal = () => {
    // Event ID 3 — Create New Course Project
    gtmTrackAuthoringCourseContentUserInteraction({
      eventCategory: GTM_EVENT_CATEGORY.DASHBOARD,
      eventAction: GTM_EVENT_ACTION.CLICK_CREATE_NEW_COURSE_PROJECT,
      eventLabel: 'Create New Course Project',
    });
    setCreateProjectModalVisible(true);
  };

  const closeCreateProjectModal = () => {
    setCreateProjectModalVisible(false);
  };

  const openMoveProjectModal = () => {
    setMoveProjectModalVisible(true);
  };

  const closeMoveProjectModal = () => {
    setMoveProjectModalVisible(false);
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmVisible(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmVisible(false);
  };

  const handleMoveProject = async (workspaceId) => {
    await projectService.moveProject(activeProjectId, workspaceId);
    closeMoveProjectModal();
    getProjectsList();
  };

  const handleDeleteProject = async () => {
    await projectService.deleteProject(activeProjectId);
    closeDeleteConfirm();
    getProjectsList();
  };

  const handleProjectGridCellClick = (project) => {
    // Event ID 2 — Open Course Project
    setCoursewareContext({ courseId: project.id });
    gtmTrackAuthoringDashboardUserInteraction({
      eventCategory: GTM_EVENT_CATEGORY.DASHBOARD,
      eventAction: GTM_EVENT_ACTION.CLICK_OPEN_COURSE_PROJECT,
      eventLabel: project.name,
    });
  };

  const handleFavoritesToggle = () => {
    setIsFavoritesExpanded((open) => !open);
  };

  const handleAllProjectsToggle = () => {
    setIsAllProjectsExpanded((open) => {
      const next = !open;
      if (workspaceId) {
        saveAllProjectsExpanded(workspaceId, next);
      }
      return next;
    });
  };

  const handleProjectFavoriteClick = (project, listSection) => {
    if (listSection === 'main') {
      setFavoriteProjects((prev) => {
        const exists = prev.some((p) => p.id === project.id);
        const next = exists ? prev.filter((p) => p.id !== project.id) : [...prev, project];
        if (workspaceId) {
          saveFavoriteProjectIds(workspaceId, next.map((p) => p.id));
        }
        return next;
      });
    } else if (listSection === 'favorites') {
      setFavoriteProjects((prev) => {
        const next = prev.filter((p) => p.id !== project.id);
        if (workspaceId) {
          saveFavoriteProjectIds(workspaceId, next.map((p) => p.id));
        }
        return next;
      });
    }
  };

  const renderProjectGrid = (projectList, listSection) => (
    <Grid>
      {[...projectList]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((project) => (
          <GridCell key={`${project.id}-${listSection}-cell`} span={{ desktop: 4, tablet: 6, mobile: 12 }} >
            <ProjectRoute to={`/courseware/${project.id}`} onClick={() => handleProjectGridCellClick(project)}>
              <ProjectCard
                permissionLevel={project.permissionLevel}
                projectId={project.id}
                name={project.name}
                createdAt={project.createdAt}
                onActionsMenuItemClick={handleActionMenuItemClick}
                favoriteIconId="push_pin"
                favoriteIconClassName={
                  listSection === 'favorites' || favoriteProjects.some((p) => p.id === project.id)
                    ? 'material-icons'
                    : 'material-icons-outlined'
                }
                onFavoriteClick={() => handleProjectFavoriteClick(project, listSection)}
              />
            </ProjectRoute>
            <Route path="/courseware/:projectId" component={ProjectActivityList} />
          </GridCell>
        ))}
    </Grid>
  );

  const getContent = () => {
    if (typeof projects === 'undefined') {
      return (
        <PlaceholderPageContent>
          <PlaceholderCardList />
        </PlaceholderPageContent>
      );
    }

    return projectsArray.length
      ? (
        <Fragment>
          {isTabHeaderVisible && <TabHeader></TabHeader>}
          <PageActions>
            <PageActionsRight>
              <Button primary onClick={openCreateProjectModal}>
                <MaterialIcon id="add" left />
                {intl.formatMessage(messages.newProject)}
              </Button>
            </PageActionsRight>
          </PageActions>
          <PageContent>
            <FavoritesContainer>
              <MainProjectsToggleHeader
                type="button"
                aria-expanded={isFavoritesExpanded}
                onClick={handleFavoritesToggle}
              >
                <MaterialIcon id={isFavoritesExpanded ? 'expand_less' : 'expand_more'} />
                <MainProjectsToggleTitle>{intl.formatMessage(messages.favorites)}</MainProjectsToggleTitle>
              </MainProjectsToggleHeader>
              {isFavoritesExpanded && (
                favoriteProjects.length === 0 ? (
                  <FavoritesEmptyHint>{intl.formatMessage(messages.addFavorites)}</FavoritesEmptyHint>
                ) : (
                  renderProjectGrid(favoriteProjects, 'favorites')
                )
              )}
            </FavoritesContainer>
            <MainProjectsCollapsibleSection>
              <MainProjectsToggleHeader
                type="button"
                aria-expanded={isAllProjectsExpanded}
                onClick={handleAllProjectsToggle}
              >
                <MaterialIcon id={isAllProjectsExpanded ? 'expand_less' : 'expand_more'} />
                <MainProjectsToggleTitle>{intl.formatMessage(messages.allProjects)}</MainProjectsToggleTitle>
              </MainProjectsToggleHeader>
              {isAllProjectsExpanded && renderProjectGrid(projectsArray, 'main')}
            </MainProjectsCollapsibleSection>
          </PageContent>
        </Fragment>
      ) : (
        <PageContent>
          <EmptyPage
            title={intl.formatMessage(messages.emptyPageTitle)}
            description={intl.formatMessage(messages.emptyPageDescription)}
            linkUrl="#"
            linkText={intl.formatMessage(messages.emptyPageLinkText)}
            buttonText={intl.formatMessage(messages.emptyPageButtonText)}
            buttonCallback={openCreateProjectModal}
          />
        </PageContent>
      );
  };

  return (
    <PageWrapper>
      <Helmet
        title={intl.formatMessage(messages.title)}
        description={intl.formatMessage(messages.description)}
      />
      <Sidebar navigation={<HomeNavigation selectedKey="courseware" />} />
      <PageViewport>
        {isTabHeaderVisible ? <WorkspaceHeader title={selectedWorkspace && selectedWorkspace.name} />
          : <WorkspaceHeader title={intl.formatMessage(messages.title)} />}
        {getContent()}
      </PageViewport>
      <HelpMenu helpContext={helpContext.PROJECTS} />
      <CreateProjectModal visible={isCreateProjectModalVisible} onDismiss={closeCreateProjectModal} />
      <MoveProjectModal visible={isMoveProjectModalVisible} onDismiss={closeMoveProjectModal} handleMoveProject={handleMoveProject} />
      <DeleteConfirm visible={isDeleteConfirmVisible} onDismiss={closeDeleteConfirm} handleDeleteProject={handleDeleteProject} />
    </PageWrapper>
  );
};

ProjectsList.propTypes = {
  getProjectsList: PropTypes.func,
  projects: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  projectService: projectServiceShape,
};

const mapStateToProps = (state) => ({
  projects: selectProjectList(state),
  workspaceList: selectWorkspacesList(state),
});

const mapDispatchToProps = (dispatch) => ({
  getProjectsList: () => dispatch(commandWorkspaceProjectList()),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const flagNames = [featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22];
export default compose(withConnect, enhanceWithServices, enhanceWithFeatureFlagSubscription)(ProjectsList, flagNames);
